/*
 * Pre-made sets in the cart (merchant, Oct 25 2026). Markup: snippets/set-cart-actions.liquid, set-gift-options.liquid.
 *
 * Edit in Cart / Edit in Builder: the set (one copy) and its paired box become single jars grouped like a Build a Box
 * (_bb id, _slot 1..n, visible "Custom Box: Box N · Slot k"); jars are charged at their own prices. A set without a
 * box stays without one (jars carry _bbn = 3/6). Cards that were for the set move to the new group ("For Box").
 * The cart shows the set's name while the jars still match a set (snippets/set-match-name.liquid).
 *
 * Make it a Gift: Gift Options panel with the right-size gift box and the Greeting Card (+ Custom Message), each
 * "Add to Set". Adding groups them with the set: _sg + "Gift Set" for sets, the group's _bb for edited sets.
 */
(() => {
  if (window.__wzSetEdit) return;
  window.__wzSetEdit = true;
  const base = window.Shopify?.routes?.root || '/';
  const getCart = async () => (await fetch(`${base}cart.js`, { credentials: 'same-origin' })).json();
  const post = async (url, body) => {
    const res = await fetch(base + url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.description || data.message || 'Cart error');
    }
    return res.json().catch(() => ({}));
  };
  const refresh = async () => {
    try {
      await customElements.whenDefined('s-cart');
      await customElements.get('s-cart')?.refreshMountedCarts?.();
    } catch (e) { /* next page load shows it */ }
  };
  const newId = (p) => p + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
  // Every spice's cart id, price, title and stock (handle -> { id, price, a, t }), cached for 5 minutes.
  let jarCache = null;
  const jarData = async () => {
    if (jarCache) return jarCache;
    try {
      const saved = JSON.parse(sessionStorage.getItem('wz-jar-data') || 'null');
      if (saved && Date.now() - saved.at < 300000) return (jarCache = saved.jars);
    } catch (e) { /* fetch below */ }
    const res = await fetch(`${base}collections/individual-spices?view=jar-data`, { credentials: 'same-origin' });
    const jars = (await res.json()).jars || {};
    jarCache = jars;
    try { sessionStorage.setItem('wz-jar-data', JSON.stringify({ at: Date.now(), jars })); } catch (e) { /* private mode */ }
    return jars;
  };
  window.WZ_jarData = jarData;
  const nameOf = (it) => String(it.properties?.['Custom Box'] || '').split(' · ')[0];
  const contentsOf = (lines) => lines
    .filter((j) => j.properties?._slot != null)
    .sort((a, b) => Number(a.properties._slot) - Number(b.properties._slot))
    .map((j) => `${j.properties._slot}. ${j.product_title}`).join(', ');

  // ---------- Edit in Cart / Edit in Builder ----------
  async function convert(wrap, mode) {
    const items = (await getCart()).items || [];
    const size = Number(wrap.dataset.size) || 3;
    const jars = (wrap.dataset.jars || '').split(',').filter(Boolean).slice(0, size).map(Number);
    const titles = (wrap.dataset.titles || '').split('|');
    if (!jars.length) throw new Error('No spices found for this set.');
    // Next free "Set N" name (was "Box N" before Oct 26 2026; old carts keep their labels).
    const names = new Set(items.map(nameOf).filter(Boolean));
    let no = new Set(items.map((i) => i.properties?._bb).filter(Boolean)).size + 1;
    while (names.has(`Set ${no}`)) no++;
    const name = `Set ${no}`;
    const bb = newId('');
    const boxVariant = Number(wrap.dataset.boxVariant) || 0;
    const add = [];
    if (boxVariant) {
      add.push({ id: boxVariant, quantity: 1, properties: { 'Custom Box': name, Contents: jars.map((v, i) => `${i + 1}. ${titles[i] || ''}`).join(', '), _bb: bb } });
    }
    jars.forEach((v, i) => add.push({
      id: v,
      quantity: 1,
      properties: { 'Custom Box': `${name} · Slot ${i + 1}`, _bb: bb, _slot: String(i + 1), _set: wrap.dataset.setTitle || '1', ...(boxVariant ? {} : { _bbn: String(size) }) },
    }));
    await post('cart/add.js', { items: add });
    // Then take out one copy of the set and its box (added first so nothing is lost on an error).
    const updates = {};
    const setLine = items.find((i) => i.key === wrap.dataset.setKey);
    if (setLine) updates[setLine.key] = Math.max(0, setLine.quantity - 1);
    if (wrap.dataset.boxKey) updates[wrap.dataset.boxKey] = 0;
    if (Object.keys(updates).length) await post('cart/update.js', { updates });
    // Cards that were for this set now go with the new group.
    const oldLabel = wrap.dataset.label;
    if (oldLabel) {
      const newLabel = `${name} · Custom ${size}-Pack`;
      const fresh = (await getCart()).items || [];
      for (const it of fresh) {
        if (it.properties?.['For Box'] === oldLabel) {
          await post('cart/change.js', { id: it.key, quantity: it.quantity, properties: { ...it.properties, 'For Box': newLabel } });
        }
      }
    }
    if (mode === 'builder') {
      window.location.href = `${wrap.dataset.builder}&edit=${encodeURIComponent(bb)}`;
      return;
    }
    await refresh();
    document.querySelector(`[data-bb-arrange-edit="${CSS.escape(bb)}"]`)?.click();
  }

  // ---------- Make it a Gift ----------
  // A loose set gets its own group first (one copy, _sg + "Gift Set": "I Love Steak Set").
  async function ensureSetGroup(wrap) {
    if (wrap.dataset.sg) return { sg: wrap.dataset.sg, label: wrap.dataset.label };
    const items = (await getCart()).items || [];
    const line = items.find((i) => i.key === wrap.dataset.setKey);
    if (!line) throw new Error('This set changed. Please refresh the page.');
    const used = new Set(items.map((i) => i.properties?.['Gift Set']).filter(Boolean));
    const title = wrap.dataset.setTitle || line.product_title || 'Set';
    const nm = /\bset$/i.test(title) ? title : `${title} Set`;
    let label = nm;
    for (let k = 2; used.has(label); k++) label = `${nm} ${k}`;
    const sg = newId('sg');
    const props = { ...line.properties, _sg: sg, 'Gift Set': label };
    if (line.quantity > 1) {
      await post('cart/change.js', { id: line.key, quantity: line.quantity - 1 });
      await post('cart/add.js', { items: [{ id: line.variant_id, quantity: 1, properties: props }] });
    } else {
      await post('cart/change.js', { id: line.key, quantity: 1, properties: props });
    }
    return { sg, label };
  }

  async function giftAdd(btn) {
    const panel = btn.closest('[data-gift-panel]');
    const wrap = btn.closest('[data-set-edit-wrap]');
    const group = wrap ? null : btn.closest('[data-bb-group]');
    const kind = btn.dataset.giftAdd;
    const msgOn = kind === 'card' && panel.querySelector(`[data-addon-group="${CSS.escape(btn.dataset.msgGroup || '')}"]`)?.getAttribute('aria-checked') === 'true';
    let label = '';
    let tagId = '';
    const items = [];
    if (wrap) {
      const g = await ensureSetGroup(wrap);
      label = g.label;
      tagId = g.sg;
      if (kind === 'box') items.push({ id: Number(btn.dataset.variant), quantity: 1, properties: { _sg: g.sg, 'Gift Set': g.label } });
    } else if (group) {
      const bb = group.dataset.bbGroup;
      const name = group.dataset.bbName || 'Set';
      label = `${name} · Custom ${group.dataset.bbSize}-Pack`;
      tagId = bb;
      if (kind === 'box') {
        const lines = ((await getCart()).items || []).filter((i) => i.properties?._bb === bb);
        items.push({ id: Number(btn.dataset.variant), quantity: 1, properties: { 'Custom Box': name, Contents: contentsOf(lines), _bb: bb } });
      }
    }
    if (kind === 'card') {
      if (msgOn) {
        items.push({ id: Number(btn.dataset.variant), quantity: 1 });
        items.push({ id: Number(btn.dataset.msgVariant), quantity: 1, properties: { _card: `${tagId}-1`, 'For Box': label } });
      } else {
        // A card without a message still belongs to this set.
        items.push({ id: Number(btn.dataset.variant), quantity: 1, properties: { 'For Box': label } });
      }
    }
    if (items.length) await post('cart/add.js', { items });
    // Keep the panel open after the cart redraws so the other option is one tap away.
    try { sessionStorage.setItem('wz-gift-reopen', JSON.stringify({ id: panel.dataset.giftPanel, h: panel.dataset.handles, t: Date.now() })); } catch (e) {}
    await refresh();
    reopen();
  }

  // Tapping "✓ Added" again removes that option (merchant, Oct 25 2026). Box: the jars stay as a box-less set (_bbn),
  // so the cart shows the jar-only picture. Card: removes the set's card; a written message asks first (keep = it stays
  // in the cart as its own unassigned card, _loose; no = the message and its card go).
  const groupLabel = (panel) => {
    const wrap = panel.closest('[data-set-edit-wrap]');
    if (wrap) return wrap.dataset.label || '';
    const group = panel.closest('[data-bb-group]');
    return group ? `${group.dataset.bbName || 'Set'} · Custom ${group.dataset.bbSize}-Pack` : '';
  };
  async function giftRemove(btn, keepMsg) {
    const panel = btn.closest('[data-gift-panel]');
    const wrap = btn.closest('[data-set-edit-wrap]');
    const group = wrap ? null : btn.closest('[data-bb-group]');
    const items = (await getCart()).items || [];
    const updates = {};
    if (btn.dataset.giftRemove === 'box') {
      if (group) {
        const bb = group.dataset.bbGroup;
        const size = String(group.dataset.bbSize || '');
        const lines = items.filter((i) => i.properties?._bb === bb);
        for (const j of lines.filter((i) => i.properties?._slot != null)) {
          await post('cart/change.js', { id: j.key, quantity: j.quantity, properties: { ...j.properties, _bbn: size } });
        }
        lines.filter((i) => i.properties?._slot == null && !i.properties?._card && i.handle !== 'greeting-card' && i.handle !== 'custom-message').forEach((b) => { updates[b.key] = 0; });
      } else if (wrap?.dataset.sg) {
        items.filter((i) => i.properties?._sg === wrap.dataset.sg && /gift-box/.test(i.handle)).forEach((b) => { updates[b.key] = 0; });
      }
    } else {
      const label = groupLabel(panel);
      if (!label) return;
      let cardsToDrop = 0;
      for (const it of items) {
        if (it.properties?.['For Box'] !== label) continue;
        if (it.handle === 'custom-message') {
          if (keepMsg) {
            const properties = { ...it.properties, _loose: '1' };
            delete properties['For Box'];
            await post('cart/change.js', { id: it.key, quantity: it.quantity, properties });
          } else {
            updates[it.key] = 0;
            cardsToDrop += it.quantity;
          }
        } else {
          updates[it.key] = 0;
        }
      }
      if (cardsToDrop) {
        const plain = items.find((it) => it.handle === 'greeting-card' && !it.properties?.['For Box'] && updates[it.key] === undefined);
        if (plain) updates[plain.key] = Math.max(0, plain.quantity - cardsToDrop);
      }
    }
    if (Object.keys(updates).length) await post('cart/update.js', { updates });
    try { sessionStorage.setItem('wz-gift-reopen', JSON.stringify({ id: panel.dataset.giftPanel, h: panel.dataset.handles, t: Date.now() })); } catch (e) {}
    await refresh();
    reopen();
  }
  // A written message for this set? Then ask before removing the card.
  async function hasWrittenMsg(panel) {
    const label = groupLabel(panel);
    const items = (await getCart()).items || [];
    return items.some((it) => it.handle === 'custom-message' && it.properties?.['For Box'] === label && String(it.properties?.Message || '').trim());
  }

  // The green link reads "Hide Gift Options" while the dropdown is open (merchant, Oct 28 2026).
  function setGiftLink(link, isOpen) {
    if (!link) return;
    link.setAttribute('aria-expanded', String(isOpen));
    const label = isOpen ? link.dataset.hideLabel : link.dataset.showLabel;
    if (label) link.textContent = label;
  }
  function reopen() {
    let want = null;
    try { want = JSON.parse(sessionStorage.getItem('wz-gift-reopen') || 'null'); } catch (e) {}
    if (!want || Date.now() - want.t > 15000) return;
    sessionStorage.removeItem('wz-gift-reopen');
    // Reopen only the set that was changed (merchant, Nov 3 2026): identical copies share spices, so match its id;
    // a set that just got a new id (a pre-made set turned into its own group) falls back to the first with those spices.
    const panels = [...document.querySelectorAll('[data-gift-panel]')];
    const byId = want.id ? panels.filter((p) => p.dataset.giftPanel === want.id) : [];
    const firstByHandles = panels.find((p) => p.dataset.handles === want.h);
    const targets = byId.length ? byId : (firstByHandles ? panels.filter((p) => p.dataset.giftPanel === firstByHandles.dataset.giftPanel) : []);
    targets.forEach((p) => {
      p.hidden = false;
      const row = p.closest('[data-set-edit-wrap], [data-bb-group]');
      setGiftLink(row?.querySelector(`[data-gift-open="${CSS.escape(p.dataset.giftPanel)}"]`), true);
      const body = p.querySelector('.sgo-body');
      const head = p.querySelector('[data-gift-min]');
      if (body) body.hidden = false;
      head?.setAttribute('aria-expanded', 'true');
    });
  }

  const busy = async (el, fn) => {
    if (!el || el.getAttribute('aria-busy') === 'true') return;
    el.setAttribute('aria-busy', 'true');
    const err = el.matches('[data-gift-panel]') ? el.querySelector('.sgo-err') : null;
    if (err) err.hidden = true;
    try {
      await fn();
    } catch (e) {
      if (err) { err.hidden = false; } else { window.alert(e.message || 'Something went wrong. Please try again.'); }
    } finally {
      el.removeAttribute('aria-busy');
    }
  };

  document.addEventListener('click', (e) => {
    const t = e.target instanceof Element ? e.target : null;
    if (!t) return;
    const edit = t.closest('[data-set-edit]');
    if (edit) {
      e.preventDefault();
      const wrap = edit.closest('[data-set-edit-wrap]');
      busy(wrap, () => convert(wrap, edit.dataset.setEdit));
      return;
    }
    // Green "Gift Options" link next to Edit (merchant, Oct 28 2026): opens and closes the dropdown.
    const open = t.closest('[data-gift-open]');
    if (open) {
      e.preventDefault();
      const row = open.closest('[data-set-edit-wrap], [data-bb-group]');
      const panel = row?.querySelector(`[data-gift-panel="${CSS.escape(open.dataset.giftOpen)}"]`);
      if (!panel) return;
      const show = panel.hidden;
      panel.hidden = !show;
      setGiftLink(open, show);
      if (show) {
        const body = panel.querySelector('.sgo-body');
        if (body) body.hidden = false;
        panel.querySelector('[data-gift-min]')?.setAttribute('aria-expanded', 'true');
      }
      return;
    }
    const close = t.closest('[data-gift-close]');
    if (close) {
      e.preventDefault();
      const panel = close.closest('[data-gift-panel]');
      const row = panel.closest('[data-set-edit-wrap], [data-bb-group]');
      panel.hidden = true;
      const link = row?.querySelector(`[data-gift-open="${CSS.escape(panel.dataset.giftPanel)}"]`);
      if (link) { link.hidden = false; setGiftLink(link, false); link.focus({ preventScroll: true }); }
      return;
    }
    const min = t.closest('[data-gift-min]');
    if (min && !min.disabled) {
      e.preventDefault();
      const body = min.closest('[data-gift-panel]')?.querySelector('.sgo-body');
      if (!body) return;
      body.hidden = !body.hidden;
      min.setAttribute('aria-expanded', String(!body.hidden));
      return;
    }
    const rem = t.closest('[data-gift-remove]');
    if (rem) {
      e.preventDefault();
      const panel = rem.closest('[data-gift-panel]');
      // Removing a box or a card (with or without a message) never asks; it just removes it.
      busy(panel, () => giftRemove(rem, false));
      return;
    }
    const pick = t.closest('[data-gift-keep-pick]');
    if (pick) {
      e.preventDefault();
      const tile = pick.closest('.sgo-tile');
      const rem2 = tile?.querySelector('[data-gift-remove="card"]');
      if (rem2) busy(pick.closest('[data-gift-panel]'), () => giftRemove(rem2, pick.dataset.giftKeepPick === '1'));
      return;
    }
    const add = t.closest('[data-gift-add]');
    if (add && !add.disabled) {
      e.preventDefault();
      busy(add.closest('[data-gift-panel]'), () => giftAdd(add));
    }
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', reopen); else reopen();

  // ---------- Adding a set = adding its jars (merchant, Oct 25 2026) ----------
  // The live set products still include the old box price, so this theme never adds the set product: it adds the
  // set's jars (own prices) as one group, plus the gift box ($4.99 / $5.99) and Greeting Card when chosen.
  // The cart names the group after the set while its jars match (snippets/set-match-name.liquid).
  const openDrawer = () => {
    const drawer = document.getElementById('cart-drawer');
    if (drawer instanceof HTMLDialogElement) {
      if (!drawer.open) drawer.showModal();
      drawer.dispatchEvent(new CustomEvent('s-cart:opened', { bubbles: true, detail: { dialogId: drawer.id } }));
    } else {
      window.location.href = `${base}cart`;
    }
  };
  window.WZ_addSetJars = async ({ setVariant, box = 0, card = 0, msg = 0 }) => {
    const set = (window.WZ_SET_JARS || {})[String(setVariant)];
    if (!set || !set.h?.length) throw new Error('This set is not available right now.');
    // Jar ids, titles and stock from the spice feed (templates/collection.jar-data.liquid).
    const jars = await jarData();
    const gone = set.h.filter((h) => !jars[h] || !jars[h].a || !jars[h].id);
    if (gone.length) {
      const names = gone.map((h) => jars[h]?.t || h).join(', ');
      throw new Error(`Sorry, ${names} ${gone.length > 1 ? 'are' : 'is'} sold out right now, so this set can't be added.`);
    }
    const d = { s: set.s, n: set.n, j: set.h.map((h) => jars[h].id), t: set.h.map((h) => jars[h].t) };
    const items = (await getCart()).items || [];
    const names = new Set(items.map(nameOf).filter(Boolean));
    let no = new Set(items.map((i) => i.properties?._bb).filter(Boolean)).size + 1;
    while (names.has(`Set ${no}`)) no++;
    const name = `Set ${no}`;
    const bb = newId('');
    const size = d.s;
    const add = [];
    if (box) add.push({ id: Number(box), quantity: 1, properties: { 'Custom Box': name, Contents: d.j.map((v, i) => `${i + 1}. ${d.t[i] || ''}`).join(', '), _bb: bb } });
    d.j.forEach((v, i) => add.push({
      id: Number(v),
      quantity: 1,
      properties: { 'Custom Box': `${name} · Slot ${i + 1}`, _bb: bb, _slot: String(i + 1), _set: d.n, ...(box ? {} : { _bbn: String(size) }) },
    }));
    if (card) {
      const label = `${name} · Custom ${size}-Pack`;
      if (msg) {
        add.push({ id: Number(card), quantity: 1 });
        add.push({ id: Number(msg), quantity: 1, properties: { _card: `${bb}-1`, 'For Box': label } });
      } else {
        add.push({ id: Number(card), quantity: 1, properties: { 'For Box': label } });
      }
    }
    await post('cart/add.js', { items: add });
    await refresh();
    openDrawer();
  };
  // Any other set Add to Cart (product cards, Gifts page, quick add): add the jars instead of the set product.
  document.addEventListener('submit', async (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (e.submitter && e.submitter.getAttribute('name') === 'checkout') return;
    // Only real add-to-cart forms (never filter/sort/search forms that happen to contain product cards).
    if (!/\/cart\/add/.test(form.getAttribute('action') || '')) return;
    const id = form.querySelector('input[name="id"]')?.value;
    if (!id || !(window.WZ_SET_JARS || {})[String(id)]) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    const btn = e.submitter || form.querySelector('[name="add"]');
    if (btn?.disabled) return;
    if (btn) btn.disabled = true;
    try {
      // Gift path (?gift=1 from a "Choose a Set" link, on the set collections): quick-add includes the matching gift box.
      const giftSize = (window.WZ_SET_JARS || {})[String(id)]?.s;
      // Homepage "With Gift Box" button (Oct 27 2026) also adds the matching box.
      const wantBox = !!form.closest('[data-with-box]') || !!(window.WZ_GIFT_PATH && document.querySelector('[data-gift-switch]'));
      let giftBox = wantBox ? (window.WZ_GIFT_BOXES || {})[giftSize] || 0 : 0;
      // Backup: ask the store for the box's variant if the page couldn't supply it.
      if (!giftBox && wantBox && giftSize) {
        try {
          const p = await (await fetch(`${base}products/${giftSize === 6 ? 'large' : 'small'}-gift-box.js`, { credentials: 'same-origin' })).json();
          giftBox = (p.variants || []).find((v) => v.available)?.id || 0;
        } catch (err2) { /* add the set alone */ }
      }
      await window.WZ_addSetJars({ setVariant: id, box: giftBox });
    } catch (err) {
      window.alert(err.message || 'Could not add to cart.');
    } finally {
      if (btn && !btn.dataset.wzSoldOut) btn.disabled = false;
    }
  }, true);
  // On load (merchant, Oct 25 2026): price set cards from the spice feed, and a set with any sold-out spice
  // can't be added: its Add to Cart buttons become a disabled "Sold out".
  const money = (cents) => `$${(cents / 100).toFixed(2)}`;
  const markSoldOut = (btn) => {
    if (!btn || btn.dataset.wzSoldOut) return;
    btn.dataset.wzSoldOut = '1';
    btn.disabled = true;
    btn.setAttribute('aria-disabled', 'true');
    const label = btn.querySelector('span') || btn;
    label.textContent = window.WZ_SOLD_OUT || 'Sold out';
  };
  const applySetStock = async () => {
    const priceEls = [...document.querySelectorAll('[data-set-jars]')];
    const forms = [...document.querySelectorAll('form')].filter((f) => (window.WZ_SET_JARS || {})[String(f.querySelector('input[name="id"]')?.value)]);
    if (!priceEls.length && !forms.length) return;
    let jars;
    try { jars = await jarData(); } catch (e) { return; }
    priceEls.forEach((el) => {
      const hs = el.dataset.setJars.split(',').filter(Boolean);
      if (!hs.length || hs.some((h) => !jars[h])) return;
      const total = hs.reduce((sum, h) => sum + jars[h].price, 0);
      // Cards with a gift-path version (product-preview): normal price + price with the matching gift box.
      const n = el.querySelector('[data-set-n]');
      const g = el.querySelector('[data-set-g]');
      if (n && g) {
        n.textContent = money(total);
        g.textContent = money(total + Number(el.dataset.boxCents || 0));
        return;
      }
      el.textContent = money(total);
    });
    forms.forEach((form) => {
      const set = window.WZ_SET_JARS[String(form.querySelector('input[name="id"]').value)];
      if (!set.h.some((h) => !jars[h] || !jars[h].a)) return;
      form.querySelectorAll('button[type="submit"], button[name="add"], [type="submit"]').forEach(markSoldOut);
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', applySetStock);
  else applySetStock();
  document.addEventListener('shopify:section:load', applySetStock);
})();

// Gift tile self-check (Oct 26 2026). Product lookups by handle are capped per page, so after many cart
// changes the Gift Options box or card tile can come back empty and show "Sold out" with a wrong price.
// Tiles marked data-gfill (snippets/set-gift-options.liquid) re-check the live product here and fix the
// title, price and Add to Set button. A product that really is sold out stays sold out.
(() => {
  const cache = {};
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const get = (handle) => {
    if (!cache[handle]) {
      cache[handle] = fetch(`${root}products/${handle}.js`, { headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null)
        .then((p) => { if (!p) delete cache[handle]; return p; });
    }
    return cache[handle];
  };
  const variantOf = (p) => p && (p.variants.find((v) => v.available) || p.variants[0]);

  async function fill(tile) {
    tile.dataset.gfillDone = '1';
    const fmt = tile.dataset.gfillFmt || '${{amount}}';
    const money = (cents) => fmt.replace(/\{\{\s*\w+\s*\}\}/, (cents / 100).toFixed(2));
    const p = await get(tile.dataset.gfill);
    const v = variantOf(p);
    if (!v || !tile.isConnected) return;
    let msgCents = Number(tile.dataset.gfillMsgCents) || 0;
    let msgVariant = null;
    if (tile.dataset.gfillMsg) {
      msgVariant = variantOf(await get(tile.dataset.gfillMsg));
      if (!msgCents && msgVariant) msgCents = msgVariant.price;
    }
    const title = tile.querySelector('[data-gfill-title]');
    if (title && !title.textContent.trim()) title.textContent = p.title;
    const price = tile.querySelector('[data-gfill-price]');
    if (price) {
      const base = money(v.price);
      const withMsg = msgCents ? money(v.price + msgCents) : base;
      const mode = price.dataset.gfillMode;
      if (mode === 'live') {
        price.dataset.base = base;
        price.dataset.with = withMsg;
        price.removeAttribute('data-with-pending');
        price.dataset.cardCents = String(v.price);
        const box = tile.querySelector(`[data-addon-group="${CSS.escape(price.dataset.addonPrice || '')}"]`);
        price.textContent = box && box.getAttribute('aria-checked') === 'true' ? withMsg : base;
      } else {
        price.textContent = mode === 'with' ? withMsg : base;
      }
    }
    const btn = tile.querySelector('[data-gfill-btn]');
    if (btn && p.available && v.available) {
      btn.dataset.giftAdd = btn.dataset.gfillBtn;
      btn.dataset.variant = String(v.id);
      if (!btn.dataset.msgVariant && msgVariant) btn.dataset.msgVariant = String(msgVariant.id);
      btn.removeAttribute('data-gfill-btn');
      btn.disabled = false;
      btn.textContent = btn.dataset.addLabel || btn.textContent;
    }
  }

  const scan = () => document.querySelectorAll('[data-gfill]:not([data-gfill-done])').forEach(fill);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan);
  else scan();
  new MutationObserver(() => { if (document.querySelector('[data-gfill]:not([data-gfill-done])')) scan(); })
    .observe(document.documentElement, { subtree: true, childList: true });
})();
